import React from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { formatCurrency } from "../utils/format";

// Three tooltip modes, picked automatically per row:
// 1. `groupTooltip` is true AND `services` entries carry a `groupTitle`
//    (admin/management viewing the account-level YTD/Quarterly charts) ->
//    collapse to one row per group, hiding individual accounts entirely.
// 2. `groupTooltip` is false/unset but entries still carry a `title`
//    (team_member viewing the same charts, scoped to their own accounts)
//    -> per-account rows, "Title (RawName)", same as before.
// 3. Neither (the drill-down service-trend chart's services have no
//    title/groupTitle at all) -> plain per-entry dataKey list, unchanged.
const ComboTooltip = ({ active, payload, label, services = [], groupTooltip = false }) => {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  const serviceEntries = payload.filter(p => p.dataKey !== "total");

  // Ordered by first appearance, which already matches GROUP_ORDER since
  // that's the order getAllAccounts builds `services` in.
  const groupTotals = [];
  const groupIndexByTitle = new Map();

  if (groupTooltip) {
    serviceEntries.forEach(entry => {
      const meta = services.find(s => s.name === entry.dataKey);
      if (!meta?.groupTitle) return;

      if (!groupIndexByTitle.has(meta.groupTitle)) {
        groupIndexByTitle.set(meta.groupTitle, groupTotals.length);
        groupTotals.push({ title: meta.groupTitle, total: 0, color: entry.color });
      }

      groupTotals[groupIndexByTitle.get(meta.groupTitle)].total += entry.value || 0;
    });
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-sm max-w-xs">
      <p className="text-xs text-slate-500 font-medium mb-1.5">{point.fullLabel || label}</p>

      {groupTotals.length > 0
        ? groupTotals.map(g => (
            <div key={g.title} className="flex items-center justify-between gap-4 text-xs py-0.5">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                {g.title}
              </span>
              <span className="font-medium text-slate-900 tabular-nums">{formatCurrency(g.total)}</span>
            </div>
          ))
        : serviceEntries.map(entry => {
            const meta = services.find(s => s.name === entry.dataKey);
            const hasTitle = meta?.title && meta.title !== entry.dataKey;

            return (
              <div key={entry.dataKey} className="flex items-center justify-between gap-4 text-xs py-0.5">
                <span className="flex items-center gap-1.5 text-slate-600 min-w-0">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                  <span className="truncate">
                    {hasTitle ? (
                      <>
                        <span className="text-slate-800 font-medium">{meta.title}</span>
                        <span className="text-slate-400"> ({entry.dataKey})</span>
                      </>
                    ) : (
                      entry.dataKey
                    )}
                  </span>
                </span>
                <span className="font-medium text-slate-900 tabular-nums shrink-0">{formatCurrency(entry.value)}</span>
              </div>
            );
          })}

      <div className="flex items-center justify-between gap-4 text-xs pt-1.5 mt-1.5 border-t border-slate-100">
        <span className="text-slate-700 font-medium">Total</span>
        <span className="font-semibold text-slate-900 tabular-nums">{formatCurrency(point.total)}</span>
      </div>
    </div>
  );
};

const AccountTrendChart = ({ data, services, hasOther, otherColor = "#94a3b8", height = 300, groupTooltip = false }) => {
  if (!data.length) {
    return <p className="text-slate-400 text-sm py-10 text-center">No trend data available.</p>;
  }

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#e2e8f0" />

          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#94a3b8", fontSize: 12 }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#94a3b8", fontSize: 12 }}
            tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`}
            width={60}
          />

          <Tooltip content={<ComboTooltip services={services} groupTooltip={groupTooltip} />} cursor={{ fill: "#f1f5f9" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />

          {services.map(s => (
            <Bar key={s.name} dataKey={s.name} stackId="services" fill={s.color} />
          ))}

          {hasOther && <Bar dataKey="Other" stackId="services" fill={otherColor} />}

          <Line
            type="monotone"
            dataKey="total"
            name="Total"
            stroke="#0f172a"
            strokeWidth={2}
            dot={{ r: 3, fill: "#0f172a" }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AccountTrendChart;
